// Fotos de recibos: se REDUCEN en el navegador antes de subirlas (nube, tanda 1, HOSTING-RECOMENDACION §3.3.3).
// Por qué aquí: ahorra datos del celular y espacio, sube más rápido, y en la nube (Linux) no hay `sips`: el servidor solo
// convierte lo que el navegador no pudo (vips, respaldo). Al mismo tamaño que la IA aprovecha (compartido/constantes.ts:
// 2576 px y 3.75 MP; la IA reduciría a lo mismo en el servidor, así que no se pierde nada que ella fuera a ver).
// Además el JPEG que sale del lienzo ya va girado y SIN metadatos (ni GPS ni datos del teléfono).
//
// Fallo ruidoso, pero sin bloquear al vendedor: si el navegador no puede abrir la foto (un HEIC en Chrome de Windows),
// se sube la ORIGINAL tal cual y el servidor la convierte (o avisa en rojo "no leíble por IA"). Nunca se sube algo peor.
import { FOTO_BYTES_MAXIMOS, FOTO_CALIDAD_JPEG, FOTO_LADO_MAXIMO_PX, FOTO_MEGAPIXELES_MAXIMOS } from '@cotizador/compartido';

export interface Medida {
  ancho: number;
  alto: number;
}

/** Medida a la que hay que reducir para quedar en ≤ 2576 px de lado y ≤ 3.75 MP, conservando la proporción.
 *  null = ya cabe (no se toca). */
export function medidaReducida(m: Medida): Medida | null {
  const largo = Math.max(m.ancho, m.alto);
  const mp = (m.ancho * m.alto) / 1_000_000;
  if (largo <= FOTO_LADO_MAXIMO_PX && mp <= FOTO_MEGAPIXELES_MAXIMOS) return null;
  const escala = Math.min(FOTO_LADO_MAXIMO_PX / largo, Math.sqrt(FOTO_MEGAPIXELES_MAXIMOS / mp));
  return { ancho: Math.max(1, Math.floor(m.ancho * escala)), alto: Math.max(1, Math.floor(m.alto * escala)) };
}

/** Una imagen ya abierta por el navegador (lo que hace falta para redibujarla). */
export interface ImagenAbierta extends Medida {
  /** Dibuja la imagen a esa medida y la devuelve como JPEG (null si el navegador no pudo). */
  aJpeg(destino: Medida, calidad: number): Promise<Blob | null>;
  cerrar(): void;
}
export type AbrirImagen = (f: File) => Promise<ImagenAbierta | null>;

const esHeic = (f: File) => /^image\/hei[cf]$/.test(f.type) || /\.hei[cf]$/i.test(f.name);
const esImagen = (f: File) => f.type.startsWith('image/') || esHeic(f);

/** Abre una imagen con lo que trae el navegador: createImageBitmap (gira según el EXIF) + un lienzo. */
export const abrirConNavegador: AbrirImagen = async (f) => {
  if (typeof createImageBitmap !== 'function') return null;
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(f, { imageOrientation: 'from-image' });
  } catch {
    return null; // p. ej. HEIC en un navegador que no lo lee: se sube la original
  }
  return {
    ancho: bmp.width,
    alto: bmp.height,
    async aJpeg(d, calidad) {
      if (typeof OffscreenCanvas === 'function') {
        const lienzo = new OffscreenCanvas(d.ancho, d.alto);
        const ctx = lienzo.getContext('2d');
        if (!ctx) return null;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bmp, 0, 0, d.ancho, d.alto);
        return lienzo.convertToBlob({ type: 'image/jpeg', quality: calidad });
      }
      const lienzo = document.createElement('canvas');
      lienzo.width = d.ancho;
      lienzo.height = d.alto;
      const ctx = lienzo.getContext('2d');
      if (!ctx) return null;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bmp, 0, 0, d.ancho, d.alto);
      return new Promise((r) => lienzo.toBlob(r, 'image/jpeg', calidad));
    },
    cerrar: () => bmp.close(),
  };
};

export interface FotoPreparada {
  archivo: File;
  /** Qué se hizo, para el vendedor (null = se sube tal cual). */
  cambio: string | null;
}

const nombreJpg = (nombre: string) => `${nombre.replace(/\.[^./\\]+$/, '') || 'foto'}.jpg`;

/** La foto lista para subir: reducida (y en JPEG) si hace falta; si no, la original. PDF y lo que no es imagen pasan
 *  sin tocarse. Nunca truena: lo peor es subir la original. */
export async function prepararFoto(f: File, abrir: AbrirImagen = abrirConNavegador): Promise<FotoPreparada> {
  if (!esImagen(f)) return { archivo: f, cambio: null };
  let img: ImagenAbierta | null = null;
  try {
    img = await abrir(f);
    if (!img || img.ancho < 1 || img.alto < 1) return { archivo: f, cambio: null };
    const objetivo = medidaReducida(img);
    const heic = esHeic(f);
    const pesada = f.size > FOTO_BYTES_MAXIMOS;
    if (!objetivo && !heic && !pesada) return { archivo: f, cambio: null };
    const blob = await img.aJpeg(objetivo ?? { ancho: img.ancho, alto: img.alto }, FOTO_CALIDAD_JPEG);
    if (!blob || blob.size === 0 || blob.type !== 'image/jpeg') return { archivo: f, cambio: null };
    // Sin reducir medidas y sin ser HEIC, solo vale la pena si de verdad pesa menos.
    if (!objetivo && !heic && blob.size >= f.size) return { archivo: f, cambio: null };
    const archivo = new File([blob], nombreJpg(f.name), { type: 'image/jpeg', lastModified: f.lastModified });
    const cambio = objetivo ? `foto reducida a ${objetivo.ancho} × ${objetivo.alto}` : heic ? 'foto HEIC pasada a JPEG' : 'foto aligerada';
    return { archivo, cambio };
  } catch {
    return { archivo: f, cambio: null };
  } finally {
    img?.cerrar();
  }
}
